<template>
  <v-app>
    <div class="page-container">
      <div class="modern-form schedule-appointment-no-login">
      <div class="info-section">
        <h3 class="section-title">
          <v-icon left color="#414a5b">mdi-calendar-blank</v-icon>
          Schedule Appointment
        </h3>
        
        <ServiceMessage page="scheduling" />
        
        <div class="modern-stepper">
          <v-stepper v-model="currentStep" vertical class="stepper-content">
            <v-stepper-step
              v-if="showClientSelector"
              :complete="currentStep > 0"
              step="0"
              @click="setStepIfBeyond(0)"
              class="step-header">
              Select Dentist
            </v-stepper-step>
            <v-stepper-content v-if="showClientSelector" step="0" class="step-content">
              <div class="client-selector">
                <h3 class="selector-title">Select a dental facility</h3>
                <v-select
                  :items="clientList"
                  :item-text="client => client.name"
                  :item-value="client => client.id"
                  label="Select Dental Provider"
                  @input="selectClient($event)"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
            </v-stepper-content>

            <v-stepper-step
              :complete="currentStep > 1"
              step="1"
              @click="setStepIfBeyond(1)"
              class="step-header">
              Login or Signup
            </v-stepper-step>
            <v-stepper-content step="1" class="step-content">
              <div class="login-signup-cards">
                <div class="auth-card">
                  <div class="auth-card-content">
                    <h3 class="auth-card-title">Have you created an appointment within Acrew before?</h3>
                    <p class="auth-card-text">If you have an account, or are scheduling an appointment for one of your dependents, please login to your account now to schedule your appointment, view bills, and more.</p>
                  </div>
                  <div class="auth-card-actions">
                    <v-btn
                      class="btn-primary"
                      @click="$router.push(`/login?clientId=${clientId}`)">
                      GO TO LOGIN
                    </v-btn>
                  </div>
                </div>
                <div class="auth-card">
                  <div class="auth-card-content">
                    <h3 class="auth-card-title">Are you new to Acrew?</h3>
                    <p class="auth-card-text">You can also schedule an appointment now and sign up for Acrew later.</p>
                    <p class="auth-card-note"><strong>Note:</strong> Appointments for minors must be scheduled from within a parent/guardian Acrew account. Please login or create your account and schedule from there.</p>
                  </div>
                  <div class="auth-card-actions">
                    <v-btn
                      class="btn-secondary"
                      @click="getAppointmentTypes()">
                      Sign Up Later
                    </v-btn>
                    <v-btn
                      class="btn-primary"
                      @click="$router.push(`/new_account?clientId=${clientId}`)">
                      CREATE AN ACCOUNT
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-stepper-content>

            <v-stepper-step
              :complete="currentStep > 2"
              step="2"
              @click="setStepIfBeyond(2)"
              class="step-header">
              What is the reason for the appointment?
              <span v-if="currentStep > 2" class="step-summary"> - {{ selectedAppointmentType.name }} <span class="step-detail"> ({{ selectedAppointmentType.appointmentLength }} minutes)</span></span>
            </v-stepper-step>
            <v-stepper-content step="2" class="step-content">
              <div class="appointment-type-selection">
                <v-progress-circular
                  v-if="apptTypeLoading"
                  indeterminate
                  color="primary"
                  class="loading-spinner" />
                <div v-if="!apptTypeLoading" class="appointment-type-list">
                  <div
                    v-for="(item, i) in appointmentTypes"
                    :key="i"
                    @click="selectAppointmentType(item)"
                    class="appointment-type-item"
                    :class="{ selected: selectedAppointmentType?.id === item.id }">
                    <div class="appointment-type-card">
                      {{ item.name }} ({{ item.appointmentLength }} minutes)
                    </div>
                  </div>
                </div>
              </div>
            </v-stepper-content>

            <v-stepper-step
              :complete="currentStep > 3"
              step="3"
              @click="setStepIfBeyond(3)"
              class="step-header">
              Find an appointment
            </v-stepper-step>
            <v-stepper-content step="3" class="step-content">
              <div class="appointment-finder">
                <v-progress-circular
                  v-if="availabilityLoading"
                  indeterminate
                  color="primary"
                  class="loading-spinner" />
                <div v-if="!availabilityLoading" class="finder-content">
                  <!-- No Providers Available -->
                  <div v-if="!providers.length" class="no-providers-available">
                    <div class="no-providers-icon">
                      <v-icon size="48" color="#9ca3af">mdi-account-off</v-icon>
                    </div>
                    <h4 class="no-providers-title">No Providers Available</h4>
                    <p class="no-providers-message">
                      There are currently no providers available for the selected appointment type. Please contact the office to schedule your appointment.
                    </p>
                  </div>
                  
                  <!-- Normal Provider Selection Flow -->
                  <div v-if="providers.length" class="provider-selection">
                    <h4 class="selection-title">Preferred Provider</h4>
                    <div class="provider-list">
                      <div
                        v-for="(item, i) in providers"
                        :key="i"
                        @click="selectProvider(item)"
                        class="provider-item"
                        :class="{ active: item.id === selectedProvider?.id }">
                        <div class="provider-card">
                          <div class="provider-avatar">
                            <img :src="item.imageUrl" alt="Avatar" @error="$event.target.src='/static/img/generic_person.png'">
                          </div>
                          <div class="provider-name">{{item.firstName}} {{item.lastName}}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                    
                  <div :class="{ 'date-selection': true, 'no-provider': !providers.length }">
                    <v-date-picker
                      v-model="datePickerSelection"
                      class="modern-date-picker"
                      :allowed-dates="isDateAllowed"
                      @change="dateSelected"
                      @click:month="getAvailabilityForAppointmentType"
                      ref="datepicker" />
                  </div>
                    
                  <div v-if="providers.length" class="time-selection">
                    <h4 v-if="slotsForDate" class="selection-title">Select a time slot for your appointment</h4>
                    <div v-if="slotsForDate && slotsForDate.length" class="time-slots">
                      <button
                        v-for="(item, i) in slotsForDate"
                        :key="i"
                        @click="selectTime(item)"
                        class="time-slot"
                        :class="{ selected: selectedTime === item.timeString }">
                        {{item.timeString}}
                      </button>
                      </div>
                    <div v-else-if="slotsForDate && slotsForDate.length === 0" class="no-appointments">
                      <div class="no-appointments-icon">
                        <v-icon size="48" color="#9ca3af">mdi-calendar-remove</v-icon>
                      </div>
                      <h4 class="no-appointments-title">No Available Appointments</h4>
                      <p class="no-appointments-message">
                        There are no available appointment slots for the selected date and provider. Please try selecting a different date or provider.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </v-stepper-content>

            <v-stepper-step step="4" class="step-header">Book your appointment</v-stepper-step>
            <v-stepper-content step="4" class="step-content">
              <div v-if="currentStep >= 4" class="booking-summary">
                <div class="summary-section">
                  <h4 class="summary-title">Appointment Type</h4>
                  <div class="summary-content">
                    <span class="appointment-name">{{ selectedAppointmentType.name }}</span>
                    <span class="appointment-duration"> ({{ selectedAppointmentType.appointmentLength }} minutes)</span>
                  </div>
                </div>

                <div class="summary-section">
                  <h4 class="summary-title">Date & Time</h4>
                  <div class="summary-content">
                    {{ selectedDate.toDateString() }} at {{ selectedTime }}
                  </div>
                </div>

                <div class="summary-section">
                  <h4 class="summary-title">Provider</h4>
                  <div class="summary-content">
                    {{selectedProvider.firstName}} {{selectedProvider.lastName}}
                  </div>
                </div>

                <div class="booking-actions">
                  <v-btn
                    class="btn-primary"
                    large
                    @click="bookAppointment()">
                    <v-icon left size="18">mdi-calendar-plus</v-icon>
                    Book Appointment
                  </v-btn>
                </div>
              </div>
            </v-stepper-content>
          </v-stepper>
        </div>
      </div>
    </div>
    </div>
  </v-app>
</template>

<script>
import moment from 'moment';
import DataService from '../../../_services/DataService';
import ServiceMessage from '../../components/ServiceMessage';

export default {
  name: 'ScheduleApptNoLogin',
  components: {
    ServiceMessage,
  },
  data() {
    return {
      pageName: 'Schedule Appointment',
      showClientSelector: false,
      currentStep: -1,
      dialog: false,
      clientList: [],
      clientId: null,
      clientColor: '#b0d35e',
      clientLogo: null,
      apptTypeLoading: false,
      availabilityLoading: false,
      appointmentTypes: [],
      selectedAppointmentType: '',
      attemptedAppointmentLoad: false,
      availableAppointmentSlots: [],
      slotsForDate: [],
      providers: [],
      selectedProvider: null,
      datePickerSelection: new Date().toISOString().substring(0, 7),
      selectedDate: null,
      selectedTime: '',
      selectedOperatory: null,
      timeIncrementPreference: 10,
    };
  },
  methods: {
    setStepIfBeyond(step) {
      if (this.currentStep > step) {
        this.currentStep = step;
      }
    },
    async loadClients() {
      const clients = await DataService.getAvailableClients();
      this.clientList = clients.data;

      this.currentStep = 0;
    },
    async selectClient(item) {
      this.clientId = item;
      this.currentStep = 1;

      await this.getClientPreferences();
    },
    async selectProvider(item) {
      this.selectedProvider = item;
      this.getAppointmentSlotsForCurrentDateSelection();
    },
    selectTime(item) {
      this.selectedTime = item.timeString;
      this.selectedProvider = this.providers.find((prov) => prov.id === item.providerId);
      this.selectedOperatory = item.operatoryId;

      this.currentStep = 4;
    },
    isDateAllowed(value) {
      if (!this.availableAppointmentSlots.length) {
        return false;
      }

      const [year, month, day] = value.split('-');
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return this.availableAppointmentSlots.some((slot) => {
        if (this.selectedProvider?.id > 0 && slot.providerId !== this.selectedProvider.id) {
          return false;
        }

        const [loopYear, loopMonth, loopDay] = slot.start.substring(0, 10).split('-');
        const slotDate = new Date(Number(loopYear), Number(loopMonth) - 1, Number(loopDay));
        return slotDate.getTime() === date.getTime();
      });
    },
    async getClientPreferences() {
      const response = await DataService.getClientPreferences(this.clientId);
      if (response.status === 200) {
        this.clientColor = response.data.clientColor;
        this.clientLogo = response.data.clientLogo;
      } else if (response.status === 404) {
        this.clientId = '';
        this.showClientSelector = true;

        await this.loadClients();
      }
    },
    async getAvailabilityForAppointmentType() {
      this.availabilityLoading = true;

      let startDate = new Date();
      // we've already loaded data; this load is being caused by a time selection change
      if (this.attemptedAppointmentLoad && this.$refs.datepicker) {
        startDate = new Date(this.$refs.datepicker.tableYear, this.$refs.datepicker.tableMonth, 1);
      }

      this.attemptedAppointmentLoad = true;

      const endDate = new Date(startDate);
      endDate.setMonth(startDate.getMonth() + 1, 0); // set as end of month

      const startDateString = moment(startDate).format('YYYY-MM-DD');
      const endDateString = moment(endDate).format('YYYY-MM-DD');

      const response = await DataService.getAvailabilityForAppointmentType(
        this.clientId,
        this.selectedAppointmentType.id,
        startDateString,
        endDateString,
      );
      if (response.status !== 200) {
        this.$root.$emit('raise_alert', {
          color: 'info',
          text: response.data || 'An error occurred retrieving scheduling data',
        });
        this.currentStep = this.currentStep - 1;
        this.availabilityLoading = false;
        return;
      }

      this.timeIncrementPreference = response.data.timeIncrementPreference;
      this.providers = response.data.providers;

      for (const provider of this.providers) {
        provider.imageUrl = `${window.acrew.apiUrl}/clients/${this.clientId}/providers/${provider.id}/image`;
      }

      if (this.providers.length > 1) {
        this.providers.unshift({ id: -1, firstName: 'Any', imageUrl: '/static/img/generic_person.png' });
      }

      this.selectedProvider = this.providers[0];

      this.availableAppointmentSlots = response.data.slots;

      if (this.availableAppointmentSlots.length) {
        // set to the first date (today typically)
        const firstDateString = this.availableAppointmentSlots[0].start.substring(0, 10);
        const [year, month, day] = firstDateString.split('-');
        this.selectedDate = new Date(Number(year), Number(month) - 1, Number(day));
        this.datePickerSelection = firstDateString;
        this.getAppointmentSlotsForCurrentDateSelection();
      } else {
        this.selectedDate = startDate;
        this.datePickerSelection = startDateString;
      }

      this.availabilityLoading = false;

      this.bindMonthClickHandlers();
    },
    async getAppointmentTypes() {
      this.currentStep = 2;
      this.apptTypeLoading = true;

      const response = await DataService.getSchedulableAppointmentTypes(this.clientId, { excludeMinors: true });

      this.appointmentTypes = response.data;
      this.apptTypeLoading = false;
    },
    async selectAppointmentType(item) {
      this.currentStep = 3;
      this.selectedAppointmentType = item;
      await this.getAvailabilityForAppointmentType();
    },
    getAppointmentSlotsForCurrentDateSelection() {
      const appointmentSlotsForDate = this.availableAppointmentSlots.filter((slot) => {
        if (this.selectedProvider?.id > 0 && slot.providerId !== this.selectedProvider.id) {
          return false;
        }

        const [year, month, day] = slot.start.substring(0, 10).split('-');
        const slotDate = new Date(Number(year), Number(month) - 1, Number(day));
        return slotDate.getTime() === this.selectedDate.getTime();
      });

      const appointmentTypeLength = this.selectedAppointmentType.appointmentLength;

      const uniqueSlots = {};
      for (const slot of appointmentSlotsForDate) {
        const endDate = new Date(slot.end);

        let dateCopy;
        let loop = 0; // track position and also prevent overflow

        do {
          dateCopy = new Date(slot.start);
          dateCopy.setMinutes(dateCopy.getMinutes() + this.timeIncrementPreference * loop);
          
          const slotTime = dateCopy.getTime();
          if (!uniqueSlots[slotTime]) {
            uniqueSlots[slotTime] = {
              time: slotTime,
              providerId: slot.providerId,
              operatoryId: slot.operatoryId,
            };
          }
        } while (++loop < 50 && dateCopy.setMinutes(dateCopy.getMinutes() + appointmentTypeLength) < endDate);
      }

      const anHourFromNow = new Date();
      anHourFromNow.setMinutes(anHourFromNow.getMinutes() + 60);

      this.slotsForDate = [];
      const timeSortedKeys = Object.keys(uniqueSlots).sort();
      for (const key of timeSortedKeys) {
        const slot = new Date(uniqueSlots[key].time);

        // don't allow past scheduling, and no sooner than an hour from now
        if (slot.getTime() < anHourFromNow) {
          continue;
        }

        const hourPiece = slot.getHours() % 12 || '12';
        const minutePiece = slot.getMinutes().toString().padStart(2, '0');
        const amPm = slot.getHours() >= 12 ? 'PM' : 'AM';

        this.slotsForDate.push({
          ...uniqueSlots[key],
          timeString: `${hourPiece}:${minutePiece} ${amPm}`,
        });
      }
    },
    dateSelected(date) {
      const [year, month, day] = date.split('-');
      this.selectedDate = new Date(Number(year), Number(month) - 1, Number(day));
      this.getAppointmentSlotsForCurrentDateSelection();
    },
    async bookAppointment() {
      const [, hour, minute, amPm] = this.selectedTime.match(/(\d+):(\d+) ([AP]M)/);
      let normalizedHour = Number(hour) + (amPm === 'PM' ? 12 : 0);
      if (!(normalizedHour % 12)) {
        normalizedHour -= 12;
      }

      const appointmentDateString = `${this.selectedDate.getFullYear()}-${(this.selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${(this.selectedDate.getDate()).toString().padStart(2, '0')}T${normalizedHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')}:00.000`; 

      this.$router.push({
        name: 'NewAccountAppointment',
        query: {
          clientId: this.clientId,
          appointmentType: this.selectedAppointmentType.id,
          appointmentDate: appointmentDateString,
          providerId: this.selectedProvider.id,
          operatoryId: this.selectedOperatory,
        },
      });
    },
    bindMonthClickHandlers() {
      // vuetify 2 doesn't offer us a way to capture month change via the prev/next button so... guess we're doing that ourselves
      // have to do wait for render and redo this on each load too weeeee
      setTimeout(() => {
        if (!this.$refs.datepicker?.$el) return;
        const prevBtn = this.$refs.datepicker.$el.querySelector('.v-btn[aria-label="Previous month"]');
        const nextBtn = this.$refs.datepicker.$el.querySelector('.v-btn[aria-label="Next month"]');
        if (prevBtn) prevBtn.addEventListener('click', this.getAvailabilityForAppointmentType);
        if (nextBtn) nextBtn.addEventListener('click', this.getAvailabilityForAppointmentType);
      }, 200);
    },
  },
  async mounted() {
    if (this.$route.query.clientId) {
      DataService.updateClientId(this.$route.query.clientId);
      this.clientId = this.$route.query.clientId;
      this.currentStep = 1;

      await this.getClientPreferences();
    } else if (DataService.getClientId()) {
      this.clientId = DataService.getClientId();
      this.currentStep = 1;

      await this.getClientPreferences();
    } else {
      this.showClientSelector = true;

      await this.loadClients();
    }
  },
};
</script>

<style lang="scss">
@import '@/scss/variables.scss';

$stepper-step-bg-color: #10b981;
$stepper-step-text-color: white;
$stepper-step-active-bg-color: #b0d35e;
$stepper-step-active-text-color: #414a5b;
$stepper-step-default-bg-color: #e5e7eb;
$stepper-step-default-text-color: #6b7280;
$step-summary-color: #6b7280;
$step-detail-color: #9ca3af;
$appointment-card-bg-color: white;
$appointment-card-border-color: #e2e8f0;
$appointment-type-text-color: #374151;
$provider-card-bg-color: white;
$provider-card-border-color: #e2e8f0;
$provider-name-text-color: #374151;
$time-slot-text-color: #374151;
$selection-title-color: #374151;
$date-picker-border-color: #d1d5db;
$summary-title-color: #374151;
$summary-content-color: #6b7280;
$appointment-duration-color: #9ca3af;
$auth-card-bg-color: white;
$auth-card-border-color: #e2e8f0;
$auth-card-title-color: #1f2937;
$auth-card-text-color: #4b5563;
$auth-card-note-color: #6b7280;

@mixin card-hover-effect {
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #edf2f7;
    border-color: #cbd5e0;
  }
}

@mixin card-base {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  @include card-hover-effect;
  
  @media (max-width: $breakpoint-mobile) {
    border-radius: 8px;
  }
}

.page-container {
  max-width: 1320px;
  margin: 0 auto;
  width: 100%;

  .info-section {
    @media (max-width: $breakpoint-mobile) {
      padding: 12px;
    }
  }
}

.schedule-appointment-no-login {
  padding: 16px;
  
  .modern-stepper {
    background: transparent !important;
    box-shadow: none !important;
  }
  
  .stepper-content {
    .v-stepper__step {
      &.v-stepper__step--complete {
        cursor: pointer;
      }
      
      .v-stepper__step__step {
        width: 28px !important;
        height: 28px !important;
        min-width: 28px !important;
        background-color: $stepper-step-default-bg-color !important;
        color: $stepper-step-default-text-color !important;
        font-weight: 600 !important;
        font-size: 13px !important;
        border-radius: 50% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: all 0.2s ease !important;
      }
      
      &.v-stepper__step--active {
        .v-stepper__step__step {
          background-color: $stepper-step-active-bg-color !important;
          color: $stepper-step-active-text-color !important;
          box-shadow: 0 0 0 3px rgba(176, 211, 94, 0.2) !important;
        }
      }
      
      &.v-stepper__step--complete {
        .v-stepper__step__step {
          background-color: $stepper-step-bg-color !important;
          color: $stepper-step-text-color !important;
          
          > i.v-icon.mdi-check {
            font-size: 1.15rem !important;
          }
        }
      }
    }
  }
  
  .step-header {
    @media (max-width: $breakpoint-mobile) {
      padding: 16px 0 !important;
      
      .v-stepper__label {
        font-size: 14px !important;
        line-height: 1.4 !important;
        padding-left: 8px !important;
      }
    }
    
    .step-summary {
      color: $step-summary-color;
      font-weight: 500;
      
      .step-detail {
        color: $step-detail-color;
      }
    }
  }
  
  .step-content {
    padding: 24px 0;
    
    @media (max-width: $breakpoint-mobile) {
      border-left: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  }
  
  .loading-spinner {
    display: block;
    margin: 40px auto;
  }
  
  .client-selector {
    .selector-title {
      font-size: 18px;
      font-weight: 600;
      color: $auth-card-title-color;
      margin-bottom: 16px;
    }
  }
  
  .login-signup-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    
    @media (max-width: $breakpoint-tablet) {
      grid-template-columns: 1fr;
      gap: 20px;
    }
    
    .auth-card {
      background: $auth-card-bg-color;
      border: 1px solid $auth-card-border-color;
      border-radius: 12px;
      padding: 24px;
      display: flex;
      flex-direction: column;
      
      @media (max-width: $breakpoint-mobile) {
        padding: 20px;
      }
      
      .auth-card-content {
        flex: 1;
        margin-bottom: 20px;
        
        .auth-card-title {
          font-size: 18px;
          font-weight: 600;
          color: $auth-card-title-color;
          margin: 0 0 16px 0;
          
          @media (max-width: $breakpoint-mobile) {
            font-size: 16px;
          }
        }
        
        .auth-card-text {
          font-size: 14px;
          color: $auth-card-text-color;
          line-height: 1.6;
          margin: 0 0 12px 0;
        }
        
        .auth-card-note {
          font-size: 13px;
          color: $auth-card-note-color;
          line-height: 1.5;
          margin: 0;
        }
      }
      
      .auth-card-actions {
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        
        @media (max-width: $breakpoint-mobile) {
          flex-direction: column;
          
          .v-btn {
            width: 100%;
          }
        }
      }
    }
  }
  
  .appointment-type-selection {
    .appointment-type-list {
      display: grid;
      gap: 12px;
      
      .appointment-type-item {
        cursor: pointer;
        
        &.selected .appointment-type-card {
          background: #f0f9e8 !important;
          border-color: #B0D35E !important;
          color: #414a5b !important;
          font-weight: 600 !important;
        }
        
        .appointment-type-card {
          padding: 16px 20px;
          @include card-base;
          font-size: 16px;
          font-weight: 500;
          color: $appointment-type-text-color;
          text-align: center;
          
          @media (max-width: $breakpoint-mobile) {
            padding: 12px 16px;
            font-size: 14px;
          }
        }
      }
    }
  }
  
  .appointment-finder {
    .finder-content {
      display: grid;
      grid-template-columns: 280px 1fr 1fr;
      gap: 32px;
      align-items: flex-start;
      
      @media (max-width: $breakpoint-tablet) {
        grid-template-columns: 240px 1fr 1fr;
        gap: 24px;
      }
      
      @media (max-width: $breakpoint-tablet) {
        grid-template-columns: 1fr;
        gap: 24px;
      }
      
      @media (max-width: $breakpoint-mobile) {
        gap: 20px;
      }
    }
    
    .no-providers-available {
      text-align: center;
      padding: 60px 20px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 12px;

      .no-providers-icon {
        margin-bottom: 16px;
      }

      .no-providers-title {
        font-size: 18px;
        font-weight: 600;
        color: #374151;
        margin: 0 0 12px 0;

        @media (max-width: $breakpoint-mobile) {
          font-size: 16px;
        }
      }

      .no-providers-message {
        font-size: 14px;
        color: #6b7280;
        line-height: 1.5;
        margin: 0;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;

        @media (max-width: $breakpoint-mobile) {
          font-size: 13px;
        }
      }
    }
    
    .selection-title {
      font-size: 16px;
      font-weight: 600;
      color: $selection-title-color;
      margin: 0 0 16px 0;
      
      @media (max-width: $breakpoint-mobile) {
        font-size: 14px;
      }
    }
    
    .provider-selection {
      .provider-list {
        display: grid;
        gap: 12px;
        
        @media (max-width: $breakpoint-tablet) {
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        }
        
        .provider-item {
          cursor: pointer;
          
          &.active .provider-card {
            background: #f0f9e8 !important;
            border-color: #B0D35E !important;
            
            .provider-name {
              color: #414a5b !important;
              font-weight: 600 !important;
            }
          }
          
          .provider-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 16px;
            @include card-base;
            
            @media (max-width: $breakpoint-mobile) {
              padding: 12px;
            }
            
            .provider-avatar {
              width: 60px;
              height: 60px;
              border-radius: 50%;
              overflow: hidden;
              margin-bottom: 8px;
              background: #f3f4f6;
              display: flex;
              align-items: center;
              justify-content: center;
              
              @media (max-width: $breakpoint-mobile) {
                width: 48px;
                height: 48px;
              }
              
              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }
            
            .provider-name {
              font-size: 14px;
              font-weight: 500;
              color: $provider-name-text-color;
              text-align: center;
              
              @media (max-width: $breakpoint-mobile) {
                font-size: 12px;
              }
            }
          }
        }
      }
    }
    
    .date-selection {
      margin-top: 44px;
      display: flex;
      justify-content: center;

      &.no-provider {
        margin-top: 0;
      }
      
      @media (max-width: $breakpoint-tablet) {
        margin-top: 0;
      }
      
      .modern-date-picker {
        border: 1px solid $date-picker-border-color;
        border-radius: 12px;
        overflow: hidden;
        
        .v-date-picker-table {
          height: auto !important;
          margin-bottom: 12px;
          min-height: 200px;
        }
        
        .v-date-picker-title {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          
          .v-date-picker-title__year {
            margin-right: 16px !important;
          }
          
          .v-date-picker-title__date {
            flex: 1 !important;
            text-align: right !important;
          }
        }
        
        .v-picker__title {
          padding: 16px !important;
        }
        
        .v-date-picker-years {
          max-height: 300px !important;
          
          .v-btn {
            margin: 4px 0 !important;
            border-radius: 8px !important;
            
            &.v-btn--active {
              background-color: $stepper-step-active-bg-color !important;
              color: $stepper-step-active-text-color !important;
            }
          }
        }
      }
    }
    
    .time-selection {
      .time-slots {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        gap: 12px;
        
        @media (max-width: $breakpoint-mobile) {
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 8px;
        }
        
        .time-slot {
          padding: 12px 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: $time-slot-text-color;
          cursor: pointer;
          transition: all 0.2s ease;
          
          @media (max-width: $breakpoint-mobile) {
            padding: 10px 12px;
            font-size: 12px;
          }
          
          &:hover {
            background-color: #edf2f7;
            border-color: #cbd5e0;
          }
          
          &.selected {
            background: #f0f9e8 !important;
            border-color: #B0D35E !important;
            color: #414a5b !important;
            font-weight: 600 !important;
          }
        }
      }
      
      .no-appointments {
        text-align: center;
        padding: 40px 20px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        
        .no-appointments-icon {
          margin-bottom: 16px;
        }
        
        .no-appointments-title {
          font-size: 18px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 12px 0;
          
          @media (max-width: $breakpoint-mobile) {
            font-size: 16px;
          }
        }
        
        .no-appointments-message {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
          margin: 0;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
          
          @media (max-width: $breakpoint-mobile) {
            font-size: 13px;
          }
        }
      }
    }
  }
  
  .booking-summary {
    .summary-section {
      margin-bottom: 24px;
      
      @media (max-width: $breakpoint-mobile) {
        margin-bottom: 20px;
      }
      
      .summary-title {
        font-size: 16px;
        font-weight: 600;
        color: $summary-title-color;
        margin: 0 0 8px 0;
        
        @media (max-width: $breakpoint-mobile) {
          font-size: 14px;
        }
      }
      
      .summary-content {
        color: $summary-content-color;
        font-size: 15px;
        
        @media (max-width: $breakpoint-mobile) {
          font-size: 14px;
        }
        
        .appointment-name {
          font-weight: 500;
        }
        
        .appointment-duration {
          color: $appointment-duration-color;
        }
      }
    }
    
    .booking-actions {
      display: flex;
      justify-content: center;
      padding-top: 24px;
      
      @media (max-width: $breakpoint-mobile) {
        padding-top: 20px;
        
        .v-btn {
          width: 100% !important;
          max-width: none !important;
        }
      }
    }
  }
}

.schedule-appointment-no-login .appointment-type-item.selected {
  background-color: transparent !important;
  border-color: transparent !important;
}
</style>
